// @flow
import React from 'react';
import * as StringUtil from 'app/common/lib/StringUtil';
import debounce from 'lodash/debounce';
import doesContainObjectById from 'app/common/react/components/growth/activation/NuxUtils/NuxUtils';
import i18n from 'app/common/lib/i18n';
import isEmpty from 'lodash/isEmpty';
import NuxPickerInterests from 'app/common/react/components/growth/activation/NuxPicker/NuxPickerInterests';
import resourceFactory from 'app/common/lib/ResourceFactory';
import { DEFAULT_CARD_SIZE } from 'app/common/react/components/growth/activation/NuxInterest/NuxInterest';
import { DefaultFontFamily } from 'app/common/react/components/common/FontFamily';
import { track } from 'app/common/lib/react/analytics';
import { type InterestObj } from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, Icon, IconButton, Spinner, Text } from 'gestalt';

type Props = {|
  handleInterestClickFn: (InterestObj) => () => void,
  interestFlipMessages: { [key: string]: string },
  interestsToFlip: Set<string>,
  isRenux: boolean,
  selectedInterests: Array<InterestObj>,
  suggestedSearchQuery: string,
|};

type State = {|
  userSearchQuery: string,
  cachedSearchResults: {
    [query: string]: Array<InterestObj>,
  },
  loading: boolean,
|};

const styles = {
  input: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#333',
    fontFamily: DefaultFontFamily,
    fontSize: 16,
    fontWeight: 600,
    height: '100%',
    outline: 'none',
    padding: '0',
    width: '100%',
  },
};

const SEARCH_BAR_WIDTH = 450;
const SEARCH_BAR_HEIGHT = 40;

const MAX_SEARCH_RESULTS = 5;
const SEARCH_DEBOUNCE_MS = 100;

class NuxTopicSearchBar extends React.PureComponent<Props, State> {
  state = {
    userSearchQuery: '',
    cachedSearchResults: {},
    loading: false,
  };

  gridContainerRef = React.createRef<Element>();

  onCancelButtonClick = () => {
    const { isRenux } = this.props;
    track(PCONST.thrift.EventType.CLICK, {
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
      component: PCONST.thrift.ComponentType.BROWSER,
      element: PCONST.thrift.ElementType.CLEAR_SEARCHES_BUTTON,
    });
    this.setState({ userSearchQuery: '' });
    this.handleSearchAndLoading('');
  };

  onQueryChange = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    const { isRenux } = this.props;
    const { cachedSearchResults } = this.state;
    if (event.target instanceof HTMLInputElement) {
      const newQuery = event.target.value;
      if (isEmpty(cachedSearchResults) && this.getNormalizedQuery(newQuery).length > 0) {
        track(PCONST.thrift.EventType.INPUT_CHANGE, {
          view: isRenux
            ? PCONST.thrift.ViewType.REDO_ORIENTATION
            : PCONST.thrift.ViewType.ORIENTATION,
          viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
          component: PCONST.thrift.ComponentType.BROWSER,
          element: PCONST.thrift.ElementType.SEARCH_BOX_TEXT_INPUT,
        });
      }
      this.setState({ userSearchQuery: newQuery });
      this.handleSearchAndLoading(newQuery);
    }
  };

  handleSearchAndLoading(query: string) {
    const { cachedSearchResults } = this.state;
    // Only call search if necessary
    const normalizedQuery = this.getNormalizedQuery(query);
    if (normalizedQuery.length > 0 && !cachedSearchResults[normalizedQuery]) {
      this.setState({
        loading: true,
      });
      this.callDebouncedSearchApi();
    } else {
      // if query becomes empty or something we already have results for
      this.setState({
        loading: false,
      });
    }
  }

  onQueryInputFocus = (event?: SyntheticKeyboardEvent<HTMLInputElement>) => {
    const { isRenux } = this.props;
    track(PCONST.thrift.EventType.CLICK, {
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
      component: PCONST.thrift.ComponentType.BROWSER,
      element: PCONST.thrift.ElementType.SEARCH_BOX_TEXT_INPUT,
    });
  };

  mapInterestsFollowingState(interests: Array<InterestObj>) {
    const { selectedInterests } = this.props;
    return interests.map<InterestObj>((interest) => {
      interest.is_followed = doesContainObjectById(selectedInterests, interest);
      return interest;
    });
  }

  getNormalizedQuery(query: string) {
    return query.trim().toLowerCase();
  }

  getSearchResults(): Array<InterestObj> {
    const { cachedSearchResults, userSearchQuery } = this.state;
    const normalizedQuery = this.getNormalizedQuery(userSearchQuery);
    if (normalizedQuery.length === 0) {
      return [];
    }
    if (cachedSearchResults[normalizedQuery]) {
      return cachedSearchResults[normalizedQuery].slice(0, MAX_SEARCH_RESULTS);
    }
    return [];
  }

  callDebouncedSearchApi = debounce(() => {
    const { cachedSearchResults, userSearchQuery } = this.state;
    const nuxInterestsSearchResource = resourceFactory.create('NuxInterestsSearchResource', {
      query: userSearchQuery,
    });
    // resourceFactory is needed to avoid cached calls to the resource
    nuxInterestsSearchResource
      .callGet({ showError: false }, { retries: 1 })
      .then((response) => {
        if (response.resource_response && response.resource_response.data) {
          const results = response.resource_response.data;
          const updatedCachedResults = {
            ...cachedSearchResults,
            [userSearchQuery]: results,
          };
          this.setState({
            cachedSearchResults: updatedCachedResults,
            loading: false,
          });
        }
      })
      .catch((error) => {
        const updatedCachedResults = {
          ...cachedSearchResults,
          [userSearchQuery]: [],
        };
        this.setState({
          cachedSearchResults: updatedCachedResults,
          loading: false,
        });
      });
  }, SEARCH_DEBOUNCE_MS);

  renderSearchBar() {
    const { userSearchQuery } = this.state;
    return (
      <Box
        color="lightGray"
        display="flex"
        height={SEARCH_BAR_HEIGHT}
        width={SEARCH_BAR_WIDTH}
        shape="rounded"
      >
        <Box
          alignItems="center"
          display="flex"
          flex="grow"
          height="100%"
          paddingX={4}
          position="relative"
        >
          <Box marginRight={3} flex="none">
            <Icon
              accessibilityLabel={i18n._('Search icon', 'Search icon in search box')}
              icon="search"
              size={20}
            />
          </Box>
          <input
            data-test-id="search-box-input"
            name="topicSearchBoxInput"
            onChange={this.onQueryChange}
            onFocus={this.onQueryInputFocus}
            placeholder={i18n._(
              'Search for a topic',
              "Placeholder for Search for when user hasn't typed in Search bar",
            )}
            style={styles.input}
            type="text"
            value={userSearchQuery}
          />
          {userSearchQuery.length > 0 && (
            <Box marginLeft={3} flex="none">
              <IconButton
                accessibilityLabel={i18n._(
                  'Cancel Search icon',
                  'Cancel Search icon in search box',
                )}
                icon="cancel"
                size="sm"
                onClick={this.onCancelButtonClick}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  renderResults() {
    const {
      handleInterestClickFn,
      isRenux,
      interestFlipMessages,
      interestsToFlip,
      suggestedSearchQuery,
    } = this.props;
    const { loading, userSearchQuery } = this.state;

    const searchResults = this.mapInterestsFollowingState(this.getSearchResults());

    return (
      <Box
        overflow={'hidden' /*don't allow scroll*/}
        width="100%"
        height={DEFAULT_CARD_SIZE.imageSize}
      >
        {loading && (
          <Spinner
            accessibilityLabel={i18n._(
              'Loading search results',
              'Label for a spinner that shows when waiting for a Search result',
            )}
            show
          />
        )}
        {!loading &&
          searchResults.length > 0 && (
            <div ref={this.gridContainerRef}>
              <NuxPickerInterests
                gridContainerRef={this.gridContainerRef.current}
                handleInterestClickFn={handleInterestClickFn}
                interests={searchResults}
                isRenux={isRenux}
                showTableOfContents={false}
                interestFlipMessages={interestFlipMessages}
                interestsToFlip={interestsToFlip}
              />
            </div>
          )}
        {!loading &&
          searchResults.length === 0 && (
            <Box
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="around"
              direction="column"
            >
              <Icon
                accessibilityLabel={i18n._(
                  'Search icon',
                  'Search icon used as placeholder in Search results',
                )}
                icon="search"
                color="lightGray"
                size={45}
              />
              <Text size="xl" color="gray" weight="bold">
                {userSearchQuery.length === 0
                  ? StringUtil.dangerouslyFormat(
                      i18n._(
                        'Try searching for a topic like "{{ suggestedSearchQuery }}"',
                        "Placeholder for Search for when user hasn't typed in Search bar",
                      ),
                      { suggestedSearchQuery },
                    )
                  : StringUtil.dangerouslyFormat(
                      i18n._(
                        "We couldn't find {{ userSearchQuery }}. Try another word or phrase!",
                        "Shown when we don't have results for a user's search query",
                      ),
                      { userSearchQuery },
                    )}
              </Text>
            </Box>
          )}
      </Box>
    );
  }

  render() {
    const { cachedSearchResults, userSearchQuery } = this.state;

    const userHasNotSearchedYet = userSearchQuery.length === 0 && isEmpty(cachedSearchResults);
    return (
      <Box
        display="flex"
        direction="column"
        justifyContent="between"
        alignItems="center"
        height={userHasNotSearchedYet ? 100 : 190}
      >
        {/* Show text until user has tried Search bar */ userHasNotSearchedYet && (
          <Text size="xl">
            {i18n._(
              "Still can't find what you're looking for?",
              'Introduces a search bar, which is shown underneath this text.',
            )}
          </Text>
        )}
        {this.renderSearchBar()}
        {/* Only show results after user has tried Search bar */ !userHasNotSearchedYet &&
          this.renderResults()}
      </Box>
    );
  }
}

export default NuxTopicSearchBar;
